import { DateTime } from 'luxon';

import { PickupLocationType } from '../../__generated__/graphql';
import type { IGetCarQuery } from '../services/carService/__generated__/queries.generated';

/** Operating-hours row shape returned by [GET_CAR] on `location.operatingHours`. */
export type CarOperatingHoursRow = NonNullable<
  NonNullable<NonNullable<NonNullable<IGetCarQuery['car']>['location']>['operatingHours']>[number]
>;

/** Slim OH row on location list / GetLocationLean (Flutter add-car gate parity). */
export type LocationOperatingHoursGateRow = {
  alwaysAvailable?: boolean | null;
  PickupLocationType?: string | null;
};

/**
 * Flutter `HostAddCarScreen._hasOperatingHours`: missing OH must not block Next;
 * only an explicit empty list blocks.
 */
export function locationHasOperatingHours(
  operatingHours: Array<LocationOperatingHoursGateRow | null> | null | undefined,
): boolean {
  if (operatingHours == null) {
    return true;
  }
  if (operatingHours.length === 0) {
    return false;
  }
  for (const row of operatingHours) {
    if (row == null) {
      continue;
    }
    if (row.alwaysAvailable === true) {
      return true;
    }
    const pickupType = String(row.PickupLocationType ?? '').toUpperCase();
    if (pickupType === 'HOME' || pickupType === '') {
      return true;
    }
  }
  return true;
}

/** HOME row matches Flutter car trip picker (`LocationOperatingHours` / hub scope). */
export function pickHomeOperatingHours(
  list: CarOperatingHoursRow[] | null | undefined,
): CarOperatingHoursRow | null {
  if (!list?.length) return null;
  return list.find(h => h.PickupLocationType === PickupLocationType.Home) ?? null;
}

function parseHms(s: string | null | undefined): { hour: number; minute: number } | null {
  if (s == null || s === '') return null;
  const parts = s.split(':');
  const hour = Math.min(23, Math.max(0, parseInt(parts[0] ?? '0', 10) || 0));
  const minute = Math.min(59, Math.max(0, parseInt(parts[1] ?? '0', 10) || 0));
  return { hour, minute };
}

function minutesFromHm(hour: number, minute: number): number {
  return hour * 60 + minute;
}

/**
 * Minute offsets from midnight `[0, 24*60)` that are **outside** operating hours
 * for [dayInZone] — mirrors Flutter `outOfOperatingHoursTimesFor` (30-min grid).
 */
export function outOfOperatingHoursMinutesFor(
  dayInZone: DateTime,
  hours: CarOperatingHoursRow,
  minuteStep = 30,
): Set<number> {
  if (hours.alwaysAvailable) return new Set();

  const weekday = dayInZone.weekday; // 1=Mon … 7=Sun (Luxon)

  let dayAvailable: boolean;
  let dayAlwaysAvailable: boolean;
  let dayStart: string | null | undefined;
  let dayEnd: string | null | undefined;

  switch (weekday) {
    case 1:
      dayAvailable = hours.mondayAvailable;
      dayAlwaysAvailable = hours.mondayAlwaysAvailable;
      dayStart = hours.mondayStart;
      dayEnd = hours.mondayEnd;
      break;
    case 2:
      dayAvailable = hours.tuesdayAvailable;
      dayAlwaysAvailable = hours.tuesdayAlwaysAvailable;
      dayStart = hours.tuesdayStart;
      dayEnd = hours.tuesdayEnd;
      break;
    case 3:
      dayAvailable = hours.wednesdayAvailable;
      dayAlwaysAvailable = hours.wednesdayAlwaysAvailable;
      dayStart = hours.wednesdayStart;
      dayEnd = hours.wednesdayEnd;
      break;
    case 4:
      dayAvailable = hours.thursdayAvailable;
      dayAlwaysAvailable = hours.thursdayAlwaysAvailable;
      dayStart = hours.thursdayStart;
      dayEnd = hours.thursdayEnd;
      break;
    case 5:
      dayAvailable = hours.fridayAvailable;
      dayAlwaysAvailable = hours.fridayAlwaysAvailable;
      dayStart = hours.fridayStart;
      dayEnd = hours.fridayEnd;
      break;
    case 6:
      dayAvailable = hours.saturdayAvailable;
      dayAlwaysAvailable = hours.saturdayAlwaysAvailable;
      dayStart = hours.saturdayStart;
      dayEnd = hours.saturdayEnd;
      break;
    default:
      dayAvailable = hours.sundayAvailable;
      dayAlwaysAvailable = hours.sundayAlwaysAvailable;
      dayStart = hours.sundayStart;
      dayEnd = hours.sundayEnd;
      break;
  }

  const out = new Set<number>();

  if (!dayAvailable) {
    for (let m = 0; m < 24 * 60; m += minuteStep) {
      out.add(m);
    }
    return out;
  }

  if (dayAlwaysAvailable) return out;

  const start = parseHms(dayStart ?? null);
  const end = parseHms(dayEnd ?? null);
  if (start == null || end == null) return out;

  const startMin = minutesFromHm(start.hour, start.minute);
  const endMin = minutesFromHm(end.hour, end.minute);

  for (let m = 0; m < 24 * 60; m += minuteStep) {
    if (m < startMin || m >= endMin) {
      out.add(m);
    }
  }
  return out;
}

export function isOperatingHoursFullyClosedDay(
  dayInZone: DateTime,
  hours: CarOperatingHoursRow,
  minuteStep = 30,
): boolean {
  const disabled = outOfOperatingHoursMinutesFor(dayInZone, hours, minuteStep);
  const stepCount = (24 * 60) / minuteStep;
  return disabled.size >= stepCount;
}
